import { getApiClient, handleApiResponse } from './client'
import type {
  LoginRequest,
  RegisterRequest,
  AccountRecoveryRequest,
  AccountLifecycleRequest,
  AccountLifecycleResponse,
  AuthResponse,
  UserResponse,
  ApiResponse
} from './types'

/** Tokens returned by /auth/refresh. Both values are rotated and must persist together. */
export interface RefreshTokenResponse {
  token: string
  refreshToken: string
}

/**
 * Servicio de autenticación
 * Proporciona métodos para login, registro, logout y gestión de sesión
 */
export class AuthService {
  /**
   * Iniciar sesión
   */
  static async login(credentials: LoginRequest): Promise<AuthResponse> {
    const client = getApiClient()
    const response = await client.post<ApiResponse<AuthResponse>>('/auth/login', credentials)
    return handleApiResponse(response)
  }

  /**
   * Registrar nuevo usuario
   */
  static async register(userData: RegisterRequest): Promise<AuthResponse> {
    const client = getApiClient()
    const response = await client.post<ApiResponse<AuthResponse>>('/auth/register', userData)
    return handleApiResponse(response)
  }

  /** Restore a recoverable account and receive a brand-new token pair. */
  static async recoverAccount(credentials: AccountRecoveryRequest): Promise<AuthResponse> {
    const client = getApiClient()
    const response = await client.post<ApiResponse<AuthResponse>>('/auth/account-recovery', credentials)
    return handleApiResponse(response)
  }

  /**
   * Requests the explicit account lifecycle transition. DELETE_NOW completes
   * with HTTP 204 and irreversibly erases the account; callers must not try to
   * deserialize a lifecycle response for that mode.
   */
  static async requestAccountLifecycle(request: AccountLifecycleRequest): Promise<AccountLifecycleResponse | void> {
    const client = getApiClient()
    const response = await client.delete<ApiResponse<AccountLifecycleResponse>>('/users/account', { data: request })
    if (response.status === 204) {
      return
    }
    return handleApiResponse(response)
  }

  /**
   * Cerrar sesión
   */
  static async logout(): Promise<void> {
    try {
      const client = getApiClient()
      await client.post<ApiResponse<void>>('/auth/logout')
    } catch (error) {
      // Ignorar errores de logout, solo limpiar localmente
      console.warn('Error during logout:', error)
    }
  }

  /**
   * Obtener usuario actual autenticado
   */
  static async getCurrentUser(): Promise<UserResponse> {
    const client = getApiClient()
    const response = await client.get<ApiResponse<UserResponse>>('/auth/me')
    return handleApiResponse(response)
  }

  /**
   * Refrescar token de acceso
   */
  static async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    const client = getApiClient()
    const response = await client.post<ApiResponse<RefreshTokenResponse>>('/auth/refresh', {
      refreshToken
    })
    return handleApiResponse(response)
  }

  /**
   * Validar token localmente (verificar formato y expiración)
   */
  static isTokenValid(token: string): boolean {
    if (!token) return false

    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Date.now() / 1000
      return payload.exp > currentTime
    } catch {
      return false
    }
  }

  /**
   * Extrae información del payload de un token JWT
   */
  static decodeToken<T = Record<string, unknown>>(token: string): T | null {
    if (!token) return null

    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return payload as T
    } catch {
      return null
    }
  }

  /**
   * Obtiene el tiempo restante de validez del token en segundos
   */
  static getTokenExpirationTime(token: string): number {
    if (!token) return 0

    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Date.now() / 1000
      return Math.max(0, payload.exp - currentTime)
    } catch {
      return 0
    }
  }
}
